import { createSlice } from '@reduxjs/toolkit'
import { DEFAULT_PAGE_CALLS } from 'games/Keno/GameTheme/KenoGame/keno.constants'
import { getMyBetsRoulette, placedBetRouletteGame } from '../slice-thunk/rouletteGame.thunk'
import { getWinningBetTypes } from '../GameTheme/RouletteGame/game-helpers/index'
import { BET_RESULT } from 'utils/constants/index'
import { generateClientSeed } from 'utils/common-functions/index'
import { moment } from 'config/moment.config'

const initialState = {
  placedBetData: null,
  betLock: false,
  betLoading: false,
  myBetsData: {
    count: 0,
    rows: []
  },
  myBetsLoading: false,
  showWin: false,
  recentWinning: [],
  winTypesContent: null,
  liveStatsData: {
    totalWins: 0,
    totalLost: 0,
    totalProfit: 0,
    totalWagered: 0
  },
  demoMyBets: []
}

const {
  actions: {
    setBetLock,
    setRouletteRoundEnd,
    appendMyBetsRoulette,
    setLiveStatsData,
    resetLiveStats,
    setBetWinAmt,
    setShowWin,
    demoRoulettePlaceBet,
    setDemoMyBets
  },
  reducer
} = createSlice({
  name: 'rouletteGame',
  initialState,
  reducers: {
    setBetLock: (state, action) => {
      return {
        ...state,
        betLock: false
      }
    },
    setRouletteRoundEnd: (state, action) => {
      const liveData = { ...state.liveStatsData }
      const { betAmount, winningAmount, result } = state.placedBetData || {}

      if (result === BET_RESULT.WON) {
        liveData.totalWins = (liveData.totalWins || 0) + 1
      } else if (result === BET_RESULT.LOST) {
        liveData.totalLost = (liveData.totalLost || 0) + 1
      }

      if (!isNaN(parseFloat(betAmount))) {
        liveData.totalWagered = Number((parseFloat(liveData.totalWagered || 0) + parseFloat(betAmount)))
      }

      if (!isNaN(parseFloat(winningAmount)) && !isNaN(parseFloat(betAmount)) && result !== 'push') {
        const profitDelta = (parseFloat(winningAmount) - parseFloat(betAmount))
        liveData.totalProfit = Number((parseFloat(liveData.totalProfit || 0) + parseFloat(profitDelta)))
      }

      return {
        ...state,
        showWin: true,
        betLock: false,
        recentWinning: [state.placedBetData, ...state.recentWinning].slice(0, 4),
        liveStatsData: liveData
      }
    },
    appendMyBetsRoulette: (state, action) => {
      const { nextServerSeedHash, ...data } = state.placedBetData || { nextServerSeedHash: '', data: {} }
      if (nextServerSeedHash) {
        const myBetsDataRows = [data, ...state.myBetsData?.rows]
        if (myBetsDataRows.length > DEFAULT_PAGE_CALLS) {
          myBetsDataRows.pop()
        }
        return {
          ...state,
          myBetsData: {
            count: state.myBetsData?.count + 1,
            rows: myBetsDataRows
          }
        }
      }
      return state
    },
    setLiveStatsData: (state, action) => {
      const liveData = { ...state.liveStatsData }
      const { betAmount, winningAmount, result } = action.payload || {}
      if (!result || (result !== BET_RESULT.WON && result !== BET_RESULT.LOST)) {
        return // Handle invalid result values
      }

      if (result === BET_RESULT.WON) {
        liveData.totalWins = (liveData.totalWins || 0) + 1
      } else if (result === BET_RESULT.LOST) {
        liveData.totalLost = (liveData.totalLost || 0) + 1
      }

      if (!isNaN(parseFloat(betAmount))) {
        liveData.totalWagered = Number((parseFloat(liveData.totalWagered || 0) + parseFloat(betAmount)).toFixed(2))
      }

      if (!isNaN(parseFloat(winningAmount)) && !isNaN(parseFloat(betAmount)) && result !== 'push') {
        const profitDelta = (parseFloat(winningAmount) - parseFloat(betAmount)).toFixed(2)
        liveData.totalProfit = Number((parseFloat(liveData.totalProfit || 0) + parseFloat(profitDelta)).toFixed(2))
      }
      return ({
        ...state,
        liveStatsData: liveData
      })
    },
    resetLiveStats: (state, action) => {
      const liveData = initialState.liveStatsData
      return ({
        ...state,
        liveStatsData: liveData
      })
    },
    setBetWinAmt: (state, action) => ({
      ...state,
      betWinAmt: action.payload || null
    }),
    setShowWin: (state, action) => ({
      ...state,
      showWin: action.payload
    }),
    // DEMO MODE
    demoRoulettePlaceBet: (state, action) => {
      const { winningNumber } = action.payload

      const winTypesContent = getWinningBetTypes(winningNumber)
      return {
        ...state,
        winTypesContent,
        placedBetData: action.payload,
        betLoading: false,
        betLock: true,
        showWin: false
      }
    },
    setDemoMyBets: (state, action) => {
      const updatedBet = { ...state.placedBetData, clientSeed: generateClientSeed(), serverSeed: generateClientSeed(), updatedAt: moment().format() }
      return ({
        ...state,
        demoMyBets: [updatedBet, ...state.demoMyBets]
      })
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(placedBetRouletteGame.pending, (state, action) => {
        return {
          ...state,
          showWin: false,
          betLock: true,
          betLoading: true,
          winTypesContent: null
        }
      })
      .addCase(placedBetRouletteGame.fulfilled, (state, action) => {
        const { winningNumber } = action.payload || { winningNumber: 0 }

        const winTypesContent = getWinningBetTypes(winningNumber)

        return {
          ...state,
          winTypesContent,
          placedBetData: action.payload,
          betLoading: false
        }
      })
      .addCase(placedBetRouletteGame.rejected, (state, action) => {
        return {
          ...state,
          betLock: false,
          betLoading: false
        }
      })
      .addCase(getMyBetsRoulette.pending, (state, action) => {
        return {
          ...state,
          myBetsLoading: true

        }
      })
      .addCase(getMyBetsRoulette.fulfilled, (state, action) => {
        return {
          ...state,
          myBetsData: action.payload,
          myBetsLoading: false

        }
      })
      .addCase(getMyBetsRoulette.rejected, (state, action) => {
        return {
          ...state,
          myBetsLoading: false

        }
      })
  }
})

export {
  setBetLock,
  setRouletteRoundEnd,
  appendMyBetsRoulette,
  setLiveStatsData,
  resetLiveStats,
  setBetWinAmt,
  setShowWin,
  demoRoulettePlaceBet,
  setDemoMyBets
}

export default reducer
